// src/components/sessions/AttendanceList.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

function formatDuration(seconds, t) {
  if (!seconds || seconds < 0) return t('common.notAvailable');

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return t('time.duration.hoursMinutesSeconds', { hours, minutes, seconds: secs });
  } else if (minutes > 0) {
    return t('time.duration.minutesSeconds', { minutes, seconds: secs });
  } else {
    return t('time.duration.seconds', { seconds: secs });
  }
}

function formatTime(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

function ConnectionTimeline({ events, t }) {
  if (!events || events.length === 0) return null;

  return (
    <div className="ml-7 mt-1.5 mb-2 space-y-1">
      {events.map((event, i) => (
        <div key={event.id || i} className="flex items-center gap-1.5 text-xs">
          <span className="font-mono text-slate-500 tabular-nums">{formatTime(event.joined_at)}</span>
          <ArrowRightIcon className="w-2.5 h-2.5 text-slate-300 flex-shrink-0" />
          <span className="font-mono text-slate-500 tabular-nums">
            {event.left_at ? formatTime(event.left_at) : (
              <span className="text-green-600 font-sans font-medium">{t('attendance.connections.active')}</span>
            )}
          </span>
          {event.duration_seconds != null && (
            <span className="text-slate-400 font-normal">
              ({formatDuration(event.duration_seconds, t)})
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function ParticipantRow({ participant, t }) {
  const [showEvents, setShowEvents] = useState(false);
  const hasEvents = participant.connection_events && participant.connection_events.length > 0;
  const multipleConnections = hasEvents && participant.connection_events.length > 1;

  if (!participant.attended) {
    return (
      <div className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-red-50">
        <div className="flex items-center gap-2">
          <XCircleIcon className="w-4 h-4 text-red-600" />
          <span className="text-sm text-slate-500">{participant.display_name}</span>
        </div>
        <span className="text-xs text-red-600">{t('attendance.absent')}</span>
      </div>
    );
  }

  return (
    <div>
      <div
        className={`flex items-center justify-between py-1.5 px-2 rounded hover:bg-green-50 ${hasEvents ? 'cursor-pointer' : ''}`}
        onClick={hasEvents ? () => setShowEvents(!showEvents) : undefined}
      >
        <div className="flex items-center gap-2">
          <CheckCircleIcon className="w-4 h-4 text-green-600" />
          <span className="text-sm text-slate-700">{participant.display_name}</span>
          {multipleConnections && (
            <span className="text-xs px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded">
              {t('attendance.connections.reconnects', { count: participant.connection_events.length })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {participant.total_duration_seconds != null && (
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <ClockIcon className="w-3 h-3" />
              {formatDuration(participant.total_duration_seconds, t)}
            </div>
          )}
          {hasEvents && (
            <div className="text-slate-400">
              {showEvents ? <ChevronDownIcon className="w-3.5 h-3.5" /> : <ChevronRightIcon className="w-3.5 h-3.5" />}
            </div>
          )}
        </div>
      </div>
      {showEvents && <ConnectionTimeline events={participant.connection_events} t={t} />}
    </div>
  );
}

export default function AttendanceList({ attendanceDetails, sessionId }) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!attendanceDetails || attendanceDetails.length === 0) {
    return null;
  }

  const teachers = attendanceDetails.filter(p => p.role === 'teacher');
  const students = attendanceDetails.filter(p => p.role === 'student');

  const attendedTeachers = teachers.filter(t => t.attended).sort((a, b) => a.display_name.localeCompare(b.display_name));
  const absentTeachers = teachers.filter(t => !t.attended).sort((a, b) => a.display_name.localeCompare(b.display_name));
  const attendedStudents = students.filter(s => s.attended).sort((a, b) => a.display_name.localeCompare(b.display_name));
  const absentStudents = students.filter(s => !s.attended).sort((a, b) => a.display_name.localeCompare(b.display_name));

  const totalAttended = attendedTeachers.length + attendedStudents.length;
  const totalParticipants = attendanceDetails.length;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <span className="font-medium text-sm text-slate-700">
            {t('attendance.title')}
          </span>
          <span className="text-xs px-2 py-1 bg-white rounded-full text-slate-600">
            {t('attendance.summary', { attended: totalAttended, total: totalParticipants })}
          </span>
          {attendedTeachers.length > 0 && (
            <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
              {t('attendance.teacherCount', { count: attendedTeachers.length })}
            </span>
          )}
          {attendedStudents.length > 0 && (
            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
              {t('attendance.studentCount', { count: attendedStudents.length })}
            </span>
          )}
        </div>
        <div className="text-slate-400">
          {isExpanded ? <ChevronDownIcon className="w-5 h-5" /> : <ChevronRightIcon className="w-5 h-5" />}
        </div>
      </button>

      {isExpanded && (
        <div className="p-4 space-y-4 bg-white">
          {teachers.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                {t('attendance.sections.teachers')}
              </h4>
              <div className="space-y-0.5">
                {attendedTeachers.map(teacher => (
                  <ParticipantRow key={teacher.institution_member_id} participant={teacher} t={t} />
                ))}
                {absentTeachers.map(teacher => (
                  <ParticipantRow key={teacher.institution_member_id} participant={teacher} t={t} />
                ))}
              </div>
            </div>
          )}

          {students.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                {t('attendance.sections.students')}
              </h4>
              <div className="space-y-0.5 max-h-80 overflow-y-auto">
                {attendedStudents.map(student => (
                  <ParticipantRow key={student.institution_member_id} participant={student} t={t} />
                ))}
                {absentStudents.map(student => (
                  <ParticipantRow key={student.institution_member_id} participant={student} t={t} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
